//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { useEffect, useRef, useState } from 'react';

interface IClosableElement<T extends HTMLElement> {
  isActive: boolean;
  elementRef: React.RefObject<T>;
}

/**
 * A hook to track whether a given click is inside or outside of the connected element's
 * DOM tree. Used to add close or unfocus behavior on an outside click.
 *
 * @param onClickOutside an optional method to call when an outside click is registered
 */
export function useOutsideClick<T extends HTMLElement>(
  onOutsideClick?: () => void,
): [
  IClosableElement<T>,
  React.Dispatch<React.SetStateAction<boolean>>
] {
  const elementRef = useRef<T>(null);
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    if (isActive) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActive, onOutsideClick]);

  const handleClickOutside = (e: MouseEvent) => {
    if (!elementRef.current?.contains(e.target as Node)) {
      setIsActive(false);
      if (onOutsideClick) {
        onOutsideClick();
      }
    }
  };

  return [{ isActive, elementRef }, setIsActive];
}
